import { safeLocalStorage } from "@/lib/safeStorage";

export const GA_MEASUREMENT_ID = "G-XKP0C4GSYN";

/**
 * First-party cookie name prefixes written by the six vendors in AnalyticsScripts.
 * Third-party cookies (sc_at on snapchat.com, fr on facebook.com) cannot be deleted
 * from our origin — the vendor opt-outs below are the only lever we have there.
 */
const ANALYTICS_COOKIE_PREFIXES = [
  "_ga", // GA4 / GTM
  "_gid",
  "_gat",
  "_gcl_au", // Google Ads
  "_fbp", // Meta
  "_fbc",
  "_ttp", // TikTok
  "_tt_enable_cookie",
  "_sc", // Snap — covers _scid, _scid_r, _sctr, _sc_cspv
  "_hj", // Hotjar
  "_cs_", // Contentsquare
];

const ANALYTICS_STORAGE_PREFIXES = ["_hj", "_cs", "_ga"];

/**
 * Best-effort runtime opt-outs. Only three of the six vendors expose one:
 * Snap and Hotjar have no documented equivalent, which is why the caller also
 * reloads the page — see AnalyticsScripts.
 */
export const revokeVendorConsent = () => {
  if (typeof window === "undefined") return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- vendor globals are untyped
  const w = window as any;

  // The hardcoded property, plus any GA4 property discovered from a _ga_<id>
  // cookie. The GTM container injects its own measurement IDs that are not
  // visible from this repo, and each needs its own ga-disable flag.
  const ids = new Set<string>([GA_MEASUREMENT_ID]);
  if (typeof document !== "undefined") {
    document.cookie.split(";").forEach((raw) => {
      const match = raw.trim().match(/^_ga_([A-Z0-9]+)=/);
      if (match) ids.add(`G-${match[1]}`);
    });
  }
  ids.forEach((id) => {
    try {
      w[`ga-disable-${id}`] = true;
    } catch {
      /* GA not loaded */
    }
  });
  try {
    w.ttq?.revokeConsent?.();
    w.ttq?.disableCookie?.();
  } catch {
    /* TikTok not loaded */
  }
  try {
    w.fbq?.("consent", "revoke");
  } catch {
    /* Meta not loaded */
  }
};

/**
 * Expires every first-party analytics cookie across each domain scope we are
 * allowed to write to, and drops the matching localStorage keys.
 */
export const clearAnalyticsCookies = () => {
  if (typeof document === "undefined") return;

  const parts = window.location.hostname.split(".");
  const scopes: (string | undefined)[] = [undefined, window.location.hostname];
  for (let i = 0; i < parts.length - 1; i++) {
    scopes.push(`.${parts.slice(i).join(".")}`);
  }

  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0]?.trim();
    if (!name || !ANALYTICS_COOKIE_PREFIXES.some((p) => name.startsWith(p))) return;

    scopes.forEach((domain) => {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
        (domain ? `; domain=${domain}` : "");
    });
  });

  // Collect first — removeItem while iterating would shift the indices.
  const drop: string[] = [];
  for (let i = 0; i < safeLocalStorage.length; i++) {
    const key = safeLocalStorage.key(i);
    if (key && ANALYTICS_STORAGE_PREFIXES.some((p) => key.startsWith(p))) {
      drop.push(key);
    }
  }
  drop.forEach((key) => safeLocalStorage.removeItem(key));
};
